import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Link from '@g4/next-furniture/Link';
import { TrackClick } from '@g4/trackster';
import { hook } from '@g4/automation-hooks';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';
import ExitPopup from '@g4/next-furniture/UpdatePassword/ExitPopup';

import { useAuthentication } from '../../../../graphql';
import { useModal } from '../../../ModalProvider';

import getIcon from './navItem/getIcon';
import { LOGOUT_PATH } from '../../../../utils/authUtils';
import { useMedia, VerticalSpacing, Box, Text } from '../../../Boneyard';
import Markdown from '../../../Markdown';
import {
  CHECK_IN,
  MANAGE_TRIP,
} from '../../../../pageComponents/stadiumPage/constants';
import ARROW_ICON from '../../../../images/ic-arrow-blue.svg';

const propTypes = {
  item: PropTypes.object.isRequired,
  defaultBackgroundType: PropTypes.oneOf(['light', 'dark']),
  isMegaMenuLink: PropTypes.bool,
  showArrow: PropTypes.bool,
  isCustomMenu: PropTypes.bool,
  isMultiLineItem: PropTypes.bool,
  isOpen: PropTypes.bool,
  automationHook: PropTypes.string,
  onclick: PropTypes.func,
  isAllegiantStadiumNavItems: PropTypes.bool,
};

const defaultProps = {
  defaultBackgroundType: 'light',
  isMegaMenuLink: false,
  showArrow: false,
  isMultiLineItem: false,
  isOpen: false,
  isAllegiantStadiumNavItems: false,
};

// prettier-ignore
const StyledSpan = styled.span`
  position: relative;
  display: flex;
  flex-direction: ${props => props.displayAsColumn ? 'column' : 'row'};
  flex: 1 0 auto;
  align-items:  ${props => props.displayAsColumn ? 'flex-start' : 'center'};
  width: 100%;
  max-width: 100%;
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(props.isMegaMenuLink ? 0 : 4)};
  font-weight: 500;
  font-style: normal;
  font-size: ${props => props.style?.fontSize ? props.style.fontSize : 'normal'};
  line-height: normal;
  letter-spacing: normal;
  text-decoration: none;
  cursor: pointer;
  color: ${props => props.isMegaMenuLink ? props.theme.colors.siteHeader.megaMenu.linkColor : props.theme.colors.siteHeader.light.linkColor};
  color: ${props => props.style?.fontColor ? props.style.fontColor : ''};

  ${props => props.defaultBackgroundType === 'dark' && `color: ${props.theme.colors.siteHeader.dark.linkColor}`};
  ${props => props.isAllegiantStadiumNavItems && `color: ${props.theme.colors.siteHeader.dark.linkColor}`};

  ${props => props.theme.query.xs} {
    padding-left: 0;
    color: ${props => props.theme.colors.siteHeader.light.linkColor}
  }

  img {
    height: 20px;
    flex-shrink: 0;
    margin-right: ${props => props.theme.spacing(1)};

    transition: opacity 0.2s ease-in-out;
  }

  ${props => (props.showArrow || props.isInteractive) && `
    border-bottom: 5px solid transparent;

    border-bottom-color: ${props.isActive
      ? props.theme.colors.siteHeader.linkActive
      : 'transparent'};

    transition: all 0.2s ease-in-out;

    &:hover,
    &:focus,
    &:focus-within {
      outline: 0;
      border-bottom-color: ${props.theme.colors.siteHeader.linkActive};
    }
  `}
  ${props => (props.showArrow || props.isInteractive) && props.theme.query.xs} {
    border-bottom: 0;
  }

  ${props => props.showArrow && `
    padding-right: ${props.theme.spacing(5)};
    padding-bottom: ${props.theme.spacing(2)};
    &::after {
      content: '';
      color: ${props.isCustomMenu ? props.isAllegiantStadiumNavItems ? props.theme.colors.siteHeader.dark.linkColor : props.theme.colors.siteHeader.light.linkColor : 'inherit'};
      margin-left: ${props.theme.spacing(1)};
      margin-bottom: ${props.isMultiLineItem ? props.theme.spacing(5) : 0};

      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-top: 7px solid;

      transition: transform 0.3s ease-in-out;

      ${props.isOpen ? `
        transform: scaleY(-1);
      ` : ''}
    }
  `}
  ${props => props.showArrow && props.theme.query.xs} {
    &::after {
      border: 0;
      background-image: url(${ARROW_ICON});
      width: 17px;
      height: 17px;
    }
  }
`;

const PhoneNumber = styled.div`
  color: ${props => props.theme.colors.siteHeader.light.linkColor};
`;

const NavContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const isLogoutLink = link => link && link.path === LOGOUT_PATH;

const NavItem = ({
  item,
  isCustomMenu,
  defaultBackgroundType,
  isAllegiantStadiumNavItems,
  isMegaMenuLink,
  showArrow,
  isMultiLineItem,
  isOpen,
  onclick,
  automationHook,
  ...rest
}) => {
  const mq = useMedia();
  const isDesktop = mq.gte('md');
  const { title, style, description, icon, content, link } = item;
  const { authData, logout, iataNumber, setAuthData } = useAuthentication();
  const { isTravelAgent } = useUserContext();
  const { setShowModal, setTitle, setContent, unSetModal } = useModal();

  const showExitPopup = () => {
    setContent(
      <ExitPopup
        isMobile={mq.lte('sm')}
        closePopup={unSetModal}
        setAuthData={setAuthData}
      />
    );
    setTitle('');
    setShowModal(true);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      onclick(event);
    }
  };

  if (link && link.path !== '/<nolink>') {
    const href = link.path || link.url;
    const linkTarget = `_${link.target.toLowerCase()}`;
    const handleClick = e => {
      if (isLogoutLink(link)) {
        e.preventDefault();
        logout && logout();
      } else if (
        authData?.forceResetPasswordFlag &&
        window.location.pathname.includes('/updatepassword')
      ) {
        e.preventDefault();
        showExitPopup(href);
      }
    };

    const navigationHref =
      isTravelAgent && (item.title === CHECK_IN || item.title === MANAGE_TRIP);

    const linkUrl = link.path
      ? window.location.href.slice(0, -1) + link.path
      : link.url;

    return (
      <Link href={!navigationHref && href} target={linkTarget}>
        {({ anchorProps }) => (
          <TrackClick
            category="site_navigation_clicks"
            action={linkUrl}
            label={title}
          >
            <StyledSpan
              as="a"
              defaultBackgroundType={defaultBackgroundType}
              isAllegiantStadiumNavItems={isAllegiantStadiumNavItems}
              isMegaMenuLink={isMegaMenuLink}
              displayAsColumn={content}
              isInteractive
              {...anchorProps}
              {...hook(automationHook)}
              onClick={handleClick}
            >
              {icon && isDesktop && (
                <img src={getIcon(icon, defaultBackgroundType)} alt="" />
              )}
              {title}
              {content && <PhoneNumber>{content}</PhoneNumber>}
            </StyledSpan>
          </TrackClick>
        )}
      </Link>
    );
  }

  return (
    <>
      <StyledSpan
        defaultBackgroundType={defaultBackgroundType}
        isMegaMenuLink={isMegaMenuLink}
        isAllegiantStadiumNavItems={isAllegiantStadiumNavItems}
        displayAsColumn={content}
        style={style}
        showArrow={showArrow}
        isMultiLineItem={isMultiLineItem}
        isCustomMenu={isCustomMenu}
        onClick={onclick}
        isInteractive
        as={showArrow ? 'button' : undefined}
        isOpen={isOpen}
        {...hook(automationHook)}
        {...rest}
        tabIndex={0}
        onKeyPress={handleKeyPress}
      >
        <NavContent>
          <Box d={'flex'} justifyContent="center" alignItems="center">
            {icon && <img src={getIcon(icon, defaultBackgroundType)} alt="" />}
            {title}
            {content && <PhoneNumber>{content}</PhoneNumber>}
          </Box>

          {description && (
            <>
              {!isTravelAgent && (
                <>
                  <VerticalSpacing size={1} />
                  <Box d={'flex'} justifyContent="center" alignItems="center">
                    <img
                      src={getIcon(description.icon, defaultBackgroundType)}
                      alt=""
                    />
                    <Markdown source={description.details} />
                  </Box>
                </>
              )}
              {isTravelAgent && (
                <>
                  <VerticalSpacing size={1} />
                  <Text size={12}>
                    <Markdown
                      source={description.details}
                      data={{ iataNumber: iataNumber }}
                    />
                  </Text>
                </>
              )}
            </>
          )}
        </NavContent>
      </StyledSpan>
    </>
  );
};

NavItem.propTypes = propTypes;

NavItem.defaultProps = defaultProps;

export default NavItem;
